import { useState } from 'react';
import { ClipboardList, Copy } from 'lucide-react';
import type { AvailableSourceTableDto } from '@/api/control/missionImpactAPI.schemas';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillTemplate } from '@/lib/sourceCapacity';
import { buildTableConfig, type SelectedTableConfig } from '@/lib/sourceTableSelection';
import {
  formatTableList,
  parseTableList,
  resolveTableList,
  type NotSyncableReason,
  type TableListResolution,
} from '@/lib/tableListPaste';

/**
 * Props for the TableListPastePanel component.
 */
interface TableListPastePanelProps {
  /** All of the source's tables, including those already added to the profile. */
  tables: readonly AvailableSourceTableDto[];
  /** The target's table prefix, used for the staging-name length check. */
  tablePrefix: string;
  /** Current selection, keyed by logical name. */
  value: Map<string, SelectedTableConfig>;
  /** Callback fired with the selection after matching tables are added to it. */
  onChange: (next: Map<string, SelectedTableConfig>) => void;
  /** Reason shown for a table whose prefixed staging name would be too long. */
  nameTooLongForPrefix: string;
}

/**
 * One row of the unresolved-names table.
 */
interface UnresolvedRow {
  name: string;
  /** Whether the table is already added (informational) rather than a failure. */
  isAlreadyAdded: boolean;
  result: string;
  details: string;
}

/**
 * Lets an administrator paste a list of table logical names, selects the ones that match a
 * syncable table (case-insensitively), and reports the ones that were already added, can't be
 * synced, or don't exist, with the reason for each.
 */
export function TableListPastePanel({
  tables,
  tablePrefix,
  value,
  onChange,
  nameTooLongForPrefix,
}: TableListPastePanelProps) {
  const { t } = useLanguage();
  const strings = t.sources.tablePaste;
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [showEmptyError, setShowEmptyError] = useState(false);
  const [resolution, setResolution] = useState<TableListResolution | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedSelected, setCopiedSelected] = useState(false);

  /**
   * Returns the user-facing explanation for a table that matched but can't be synced.
   */
  const notSyncableDetail = (reason: NotSyncableReason): string =>
    reason === 'NameTooLongForPrefix'
      ? nameTooLongForPrefix
      : ((t.wizard.availableTableDisabledReasons as Record<string, string>)[reason] ?? reason);

  /**
   * Resolves the pasted names, adds the matching tables to the selection, and keeps the outcome
   * for reporting.
   */
  const handleResolve = () => {
    const names = parseTableList(text);
    if (names.length === 0) {
      setShowEmptyError(true);
      return;
    }

    const next = resolveTableList(names, tables, value, tablePrefix);
    if (next.toSelect.length > 0) {
      const selection = new Map(value);
      for (const table of next.toSelect) {
        selection.set(table.logicalName, buildTableConfig(table));
      }
      onChange(selection);
    }

    setResolution(next);
    setCopied(false);
    setIsOpen(false);
    setText('');
  };

  /**
   * Closes the input without resolving anything.
   */
  const handleCancel = () => {
    setIsOpen(false);
    setText('');
    setShowEmptyError(false);
  };

  const unresolvedRows: UnresolvedRow[] = resolution
    ? [
        ...resolution.alreadyAdded.map((name) => ({
          name,
          isAlreadyAdded: true,
          result: strings.resultAlreadyAdded,
          details: strings.alreadyAddedDetail,
        })),
        ...resolution.notSyncable.map(({ logicalName, reason }) => ({
          name: logicalName,
          isAlreadyAdded: false,
          result: strings.resultNotSyncable,
          details: notSyncableDetail(reason),
        })),
        ...resolution.notFound.map(({ name, suggestion }) => ({
          name,
          isAlreadyAdded: false,
          result: strings.resultNotFound,
          details: suggestion ? fillTemplate(strings.didYouMean, { name: suggestion }) : strings.noMatch,
        })),
      ]
    : [];

  /**
   * Copies the names that were not selected (can't be synced or not found) to the clipboard, one
   * per line, so the administrator can fix and re-paste them.
   */
  const handleCopyUnresolved = () => {
    if (!resolution) return;
    const names = [
      ...resolution.notSyncable.map((row) => row.logicalName),
      ...resolution.notFound.map((row) => row.name),
    ];
    void navigator.clipboard.writeText(names.join('\n')).then(() => { setCopied(true); });
  };

  /**
   * Copies the logical names of the tables selected so far to the clipboard, comma-separated, so
   * the list can be saved or pasted into another profile.
   */
  const handleCopySelected = () => {
    void navigator.clipboard.writeText(formatTableList(value.keys())).then(() => {
      setCopiedSelected(true);
      setTimeout(() => { setCopiedSelected(false); }, 2000);
    });
  };

  const hasCopyableNames = !!resolution && resolution.notSyncable.length + resolution.notFound.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {isOpen ? (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          <p className="text-sm text-muted-foreground">{strings.description}</p>
          <Textarea
            aria-label={strings.open}
            placeholder={strings.placeholder}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setShowEmptyError(false);
            }}
            rows={6}
            autoFocus
          />
          {showEmptyError && <p className="text-sm text-destructive">{strings.emptyInput}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              {strings.cancel}
            </Button>
            <Button type="button" size="sm" onClick={handleResolve}>
              {strings.resolve}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setIsOpen(true); }}
            disabled={tables.length === 0}
          >
            <ClipboardList className="h-4 w-4" />
            {strings.open}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopySelected}
            disabled={value.size === 0}
          >
            <Copy className="h-4 w-4" />
            {copiedSelected ? strings.copied : strings.copySelected}
          </Button>
        </div>
      )}

      {resolution && (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          <p className="text-sm">
            {fillTemplate(strings.summary, {
              selected: resolution.toSelect.length,
              alreadySelected: resolution.alreadySelected.length,
              alreadyAdded: resolution.alreadyAdded.length,
              notSyncable: resolution.notSyncable.length,
              notFound: resolution.notFound.length,
            })}
          </p>
          {unresolvedRows.length > 0 && (
            <div className="max-h-[200px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{strings.columnTable}</TableHead>
                    <TableHead>{strings.columnResult}</TableHead>
                    <TableHead>{strings.columnDetails}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unresolvedRows.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell className="font-mono text-xs">{row.name}</TableCell>
                      <TableCell>
                        <Badge variant={row.isAlreadyAdded ? 'secondary' : 'outline'}>
                          {row.result}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-normal text-xs text-muted-foreground">
                        {row.details}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="flex justify-end gap-2">
            {hasCopyableNames && (
              <Button type="button" variant="outline" size="sm" onClick={handleCopyUnresolved}>
                {copied ? strings.copied : strings.copyUnresolved}
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => { setResolution(null); }}>
              {strings.dismiss}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
